import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SearchIcon from '@mui/icons-material/Search';

import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import OrderStatusChip from '../../components/OrderStatusChip';
import useProjectsData from '../../hooks/useProjectsData';
import { DEPARTMENTS, PROJECT_JOBS, ProjectJob } from '../../data/projectsData';
import {
  assignmentsForJob,
  departmentColor,
  departmentLabel,
  formatCurrency,
  formatDateDisplay,
  jobsByDepartment,
  ordersForJob,
  scheduledHours,
} from '../../utils/projectsUtils';

interface SummaryCardProps {
  label: string;
  value: string;
  caption?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, caption }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight="bold">
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const HoursProgress: React.FC<{ job: ProjectJob }> = ({ job }) => {
  const used = job.total_hrs - job.hrs_remain;
  const percent = job.total_hrs > 0 ? Math.round((used / job.total_hrs) * 100) : 0;

  return (
    <Box sx={{ width: '100%', pr: 2 }}>
      <Typography variant="caption">
        {used.toLocaleString()} / {job.total_hrs.toLocaleString()} hrs ({percent}%)
      </Typography>
      <LinearProgress variant="determinate" value={Math.min(100, percent)} sx={{ height: 6, borderRadius: 3 }} />
    </Box>
  );
};

const columns = [
  { name: 'Job ID', selector: (row: ProjectJob) => row.job_id, sortable: true, maxWidth: '120px' },
  {
    name: 'Customer',
    selector: (row: ProjectJob) => row.company || row.description,
    sortable: true,
    grow: 2,
  },
  {
    name: 'Department',
    selector: (row: ProjectJob) => row.dept_id,
    sortable: true,
    maxWidth: '160px',
    cell: (row: ProjectJob) => (
      <Chip
        label={departmentLabel(row.dept_id)}
        size="small"
        sx={{ backgroundColor: departmentColor(row.dept_id), color: 'white', fontWeight: 600 }}
      />
    ),
  },
  {
    name: 'Coordinator',
    selector: (row: ProjectJob) => row.project_coordinator || '—',
    sortable: true,
    maxWidth: '130px',
  },
  {
    name: 'Start',
    selector: (row: ProjectJob) => row.date_start,
    sortable: true,
    maxWidth: '110px',
    cell: (row: ProjectJob) => formatDateDisplay(row.date_start),
  },
  {
    name: 'Contract',
    selector: (row: ProjectJob) => row.contract_amount,
    sortable: true,
    maxWidth: '120px',
    cell: (row: ProjectJob) => formatCurrency(row.contract_amount),
  },
  {
    name: 'Hours Used',
    selector: (row: ProjectJob) => row.total_hrs - row.hrs_remain,
    sortable: true,
    cell: (row: ProjectJob) => <HoursProgress job={row} />,
  },
  {
    name: 'Orders',
    selector: (row: ProjectJob) => ordersForJob(row.job_id).length,
    maxWidth: '80px',
    center: true,
  },
];

const ProjectJobsListPage: React.FunctionComponent = () => {
  const history = useHistory();
  const { source, loading, error } = useProjectsData();
  const [activeDepts, setActiveDepts] = useState<string[]>(DEPARTMENTS);
  const [search, setSearch] = useState('');
  const [coordinator, setCoordinator] = useState('all');
  const [scheduling, setScheduling] = useState('all');

  // When live data swaps in, re-sync the department filter to the real departments.
  useEffect(() => {
    setActiveDepts(DEPARTMENTS);
  }, [source]);

  const coordinators = useMemo(
    () =>
      Array.from(new Set(PROJECT_JOBS.map((job) => job.project_coordinator)))
        .filter((name) => name && name !== '*None*')
        .sort(),
    [source],
  );

  const visibleJobs = useMemo(() => {
    const query = search.trim().toLowerCase();
    return PROJECT_JOBS.filter((job) => {
      if (!activeDepts.includes(job.dept_id)) return false;
      if (coordinator !== 'all' && job.project_coordinator !== coordinator) return false;
      if (scheduling !== 'all') {
        const hasUnscheduled = ordersForJob(job.job_id).some((order) => order.unscheduled);
        if (scheduling === 'unscheduled' && !hasUnscheduled) return false;
        if (scheduling === 'scheduled' && hasUnscheduled) return false;
      }
      if (query) {
        const haystack = `${job.job_id} ${job.company} ${job.description} ${job.project_coordinator}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [activeDepts, coordinator, scheduling, search, source]);

  const totalContract = visibleJobs.reduce((total, job) => total + job.contract_amount, 0);
  const totalScheduled = visibleJobs.reduce((total, job) => total + scheduledHours(assignmentsForJob(job.job_id)), 0);
  const unscheduledOrders = visibleJobs.flatMap((job) => ordersForJob(job.job_id)).filter((order) => order.unscheduled);

  const filtersActive =
    Boolean(search) || coordinator !== 'all' || scheduling !== 'all' || activeDepts.length !== DEPARTMENTS.length;

  const clearFilters = () => {
    setSearch('');
    setCoordinator('all');
    setScheduling('all');
    setActiveDepts(DEPARTMENTS);
  };

  const toggleDept = (dept: string) =>
    setActiveDepts((prev) => (prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept]));

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        mainTitle="Project Jobs"
        subTitle="Open jobs across all project departments — ServMan Tampa"
        titleIcon={<CalendarMonthIcon />}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn’t load project data from ServMan. Refresh to try again.
        </Alert>
      )}
      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading project data…
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <SummaryCard
            label="Open Jobs"
            value={String(visibleJobs.length)}
            caption={`across ${activeDepts.length} department${activeDepts.length === 1 ? '' : 's'}`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Contract Value" value={formatCurrency(totalContract)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Scheduled Hours" value={totalScheduled.toLocaleString()} caption="currently scheduled" />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            label="Unscheduled Orders"
            value={String(unscheduledOrders.length)}
            caption="Need crew assignment"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search job ID, customer, or coordinator"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Coordinator</InputLabel>
          <Select label="Coordinator" value={coordinator} onChange={(event) => setCoordinator(event.target.value)}>
            <MenuItem value="all">All coordinators</MenuItem>
            {coordinators.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Scheduling</InputLabel>
          <Select label="Scheduling" value={scheduling} onChange={(event) => setScheduling(event.target.value)}>
            <MenuItem value="all">All jobs</MenuItem>
            <MenuItem value="unscheduled">Has unscheduled orders</MenuItem>
            <MenuItem value="scheduled">Fully scheduled</MenuItem>
          </Select>
        </FormControl>
        {filtersActive && (
          <Button size="small" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {visibleJobs.length} of {PROJECT_JOBS.length} jobs
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          Departments:
        </Typography>
        {DEPARTMENTS.map((dept) => {
          const active = activeDepts.includes(dept);
          return (
            <Chip
              key={dept}
              label={`${departmentLabel(dept)} (${jobsByDepartment(dept).length})`}
              size="small"
              onClick={() => toggleDept(dept)}
              variant={active ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 600,
                backgroundColor: active ? departmentColor(dept) : 'transparent',
                color: active ? 'white' : departmentColor(dept),
                borderColor: departmentColor(dept),
              }}
            />
          );
        })}
      </Box>

      <DataTable
        key={`${activeDepts.join(',')}|${coordinator}|${scheduling}|${search}`}
        data={visibleJobs}
        columns={columns}
        onRowClicked={(row: ProjectJob) => history.push(`/projects/jobs/${row.job_id}`)}
        striped
        highlightOnHover
        pointerOnHover
        pagination
        paginationPerPage={25}
      />
    </Box>
  );
};

export default ProjectJobsListPage;
